import { useState, useEffect } from 'react'
import useAppStatus from '../hooks/useAppStatus'
import { calculateDataSourceHealth } from '../../../shared/utils/dataSourceHealth'

const statusConfig = {
  operational: {
    color: 'text-green-500',
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    dot: 'bg-green-500',
    label: 'Operational'
  },
  degraded: {
    color: 'text-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    dot: 'bg-amber-500',
    label: 'Degraded'
  },
  outage: {
    color: 'text-red-500',
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    dot: 'bg-red-500',
    label: 'Outage'
  },
  unknown: {
    color: 'text-gray-400',
    bg: 'bg-gray-50 dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-700',
    dot: 'bg-gray-400',
    label: 'Unknown'
  },
  skipped: {
    color: 'text-gray-400',
    bg: 'bg-gray-50 dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-700',
    dot: 'bg-gray-300 dark:bg-gray-600',
    label: 'Not Checked'
  }
}

function StatusDot({ status, size = 'sm' }) {
  const config = statusConfig[status] || statusConfig.unknown
  const sizeClass = size === 'lg' ? 'w-3 h-3' : 'w-2 h-2'
  return (
    <span className={`inline-block ${sizeClass} rounded-full ${config.dot} ${status === 'operational' ? 'animate-pulse' : ''}`} />
  )
}

function OverallHero({ summary, lastChecked, loading, onRefresh }) {
  const hasOutage = summary.outage > 0
  const hasDegraded = summary.degraded > 0
  const allGood = !hasOutage && !hasDegraded

  let bgClass, iconColor, title, subtitle
  if (hasOutage) {
    bgClass = 'from-red-500 to-red-600 dark:from-red-600 dark:to-red-700'
    iconColor = 'text-red-200'
    title = `${summary.outage} Service${summary.outage > 1 ? 's' : ''} Down`
    subtitle = 'Some platform features may be unavailable'
  } else if (hasDegraded) {
    bgClass = 'from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700'
    iconColor = 'text-amber-200'
    title = `${summary.degraded} Service${summary.degraded > 1 ? 's' : ''} Degraded`
    subtitle = 'Performance may be affected'
  } else {
    bgClass = 'from-green-500 to-emerald-600 dark:from-green-600 dark:to-emerald-700'
    iconColor = 'text-green-200'
    title = 'All Systems Operational'
    subtitle = `${summary.operational} services running normally`
  }

  return (
    <div className={`bg-gradient-to-r ${bgClass} rounded-2xl p-6 text-white relative overflow-hidden`}>
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 400 120" preserveAspectRatio="none">
          <path d="M0,60 Q50,20 100,60 T200,60 T300,60 T400,60" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M0,80 Q50,40 100,80 T200,80 T300,80 T400,80" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center`}>
            {allGood ? (
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : hasOutage ? (
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            ) : (
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          <div>
            <h2 className="text-xl font-bold">{title}</h2>
            <p className="text-sm text-white/80 mt-0.5">{subtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {lastChecked && (
            <span className="text-xs text-white/60 hidden sm:block">
              {new Date(lastChecked).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-2 rounded-lg bg-white/20 hover:bg-white/30 disabled:opacity-50 transition-colors backdrop-blur-sm"
            title="Refresh status"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

function MetricCard({ label, value, sub, icon, status }) {
  const config = status ? statusConfig[status] : null
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
        <span className={`${config ? config.color : 'text-gray-400 dark:text-gray-500'}`}>
          {icon}
        </span>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</p>}
    </div>
  )
}

function ExternalLink({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
    >
      {children}
      <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  )
}

function ServiceRow({ name, description, result, statusPage }) {
  const config = statusConfig[result.status] || statusConfig.unknown
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700/50 last:border-0 group">
      <div className="flex items-center gap-3 min-w-0">
        <StatusDot status={result.status} size="lg" />
        <div className="min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-white">{name}</span>
          {description && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 hidden sm:inline">{description}</span>}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {result.latency != null && (
          <div className="text-right hidden sm:block">
            <span className={`text-xs font-mono font-medium ${result.latency < 200 ? 'text-green-600 dark:text-green-400' : result.latency < 500 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
              {result.latency}ms
            </span>
          </div>
        )}
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${config.bg} ${config.color} border ${config.border}`}>
          {config.label}
        </span>
        {statusPage && (
          <a
            href={statusPage}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-300 dark:text-gray-600 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            title={`View ${name} status page`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </div>
    </div>
  )
}

function SectionCard({ title, icon, badge, children }) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/50">
        <span className="text-gray-400 dark:text-gray-500">{icon}</span>
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h2>
        {badge && (
          <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
            {badge}
          </span>
        )}
      </div>
      <div className="px-5 py-1">
        {children}
      </div>
    </div>
  )
}

function PageGrid({ pages }) {
  return (
    <>
      {['Admin', 'Portal', 'Global Admin'].map(section => {
        const sectionPages = pages.filter(p => p.section === section)
        if (sectionPages.length === 0) return null
        return (
          <div key={section} className="py-3 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
            <h3 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5">{section}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5">
              {sectionPages.map(page => (
                <div key={page.id} className="flex items-center gap-2 py-1 group">
                  <StatusDot status="operational" />
                  <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{page.name}</span>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </>
  )
}

function IntegrationCards() {
  const integrations = [
    { name: 'Cloudflare Pages', type: 'Hosting', detail: 'Static site + Pages Functions', icon: '☁️', url: 'https://dash.cloudflare.com', statusUrl: 'https://www.cloudflarestatus.com' },
    { name: 'Supabase', type: 'Backend', detail: 'PostgreSQL, Auth, Storage, RLS', icon: '⚡', url: 'https://supabase.com/dashboard', statusUrl: 'https://status.supabase.com' },
    { name: 'Anthropic Claude', type: 'AI', detail: 'Haiku 4.5 + Sonnet 4.6', icon: '🤖', url: 'https://console.anthropic.com', statusUrl: 'https://status.anthropic.com' },
    { name: 'jsPDF', type: 'Export', detail: 'PDF report generation', icon: '📄', url: 'https://github.com/parallax/jsPDF' },
    { name: 'Recharts', type: 'Charts', detail: 'Assessment visualizations', icon: '📊', url: 'https://recharts.org' },
    { name: 'CISA KEV', type: 'Threat Intel', detail: 'Exploited vulnerabilities feed', icon: '🛡️', url: 'https://www.cisa.gov/known-exploited-vulnerabilities-catalog' },
    { name: 'MITRE ATT&CK', type: 'Threat Intel', detail: 'Threat actor & technique data', icon: '🎯', url: 'https://attack.mitre.org' }
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 py-3">
      {integrations.map((integration, i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
          <span className="text-lg flex-shrink-0 mt-0.5">{integration.icon}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white">{integration.name}</span>
              <StatusDot status="operational" />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{integration.detail}</p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{integration.type}</span>
              <div className="flex items-center gap-2 ml-auto">
                {integration.statusUrl && (
                  <a href={integration.statusUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] font-medium text-primary-500 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 uppercase tracking-wider transition-colors">
                    Status
                  </a>
                )}
                {integration.url && (
                  <a href={integration.url} target="_blank" rel="noopener noreferrer" className="text-gray-300 dark:text-gray-600 hover:text-primary-500 dark:hover:text-primary-400 transition-colors" title={`Open ${integration.name}`}>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function formatTokens(n) {
  if (!n) return '0'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toString()
}

function formatCost(n) {
  if (!n && n !== 0) return '$0.00'
  return `$${parseFloat(n).toFixed(4)}`
}

const MODEL_SHORT_NAMES = {
  'claude-opus-4-6': 'Opus 4.6',
  'claude-sonnet-4-6': 'Sonnet 4.6',
  'claude-sonnet-4-5-20250929': 'Sonnet 4.5',
  'claude-haiku-4-5-20251001': 'Haiku 4.5'
}

function AIUsageSection() {
  const [usage, setUsage] = useState(null)
  const [loadingUsage, setLoadingUsage] = useState(true)
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [creditForm, setCreditForm] = useState({ total: '', threshold: '1.00', notes: '' })
  const [savingCredit, setSavingCredit] = useState(false)

  const fetchUsage = async () => {
    setLoadingUsage(true)
    try {
      const token = (await (await import('../../../shared/lib/supabase')).supabase.auth.getSession()).data.session?.access_token
      const res = await fetch('/api/global/ai-usage', { headers: { Authorization: `Bearer ${token}` } })
      if (res.ok) setUsage(await res.json())
    } catch (e) {
      console.warn('Failed to fetch AI usage:', e)
    } finally {
      setLoadingUsage(false)
    }
  }

  useEffect(() => { fetchUsage() }, [])

  const handleSaveCredit = async () => {
    setSavingCredit(true)
    try {
      const token = (await (await import('../../../shared/lib/supabase')).supabase.auth.getSession()).data.session?.access_token
      await fetch('/api/global/ai-credit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          total_credits_usd: parseFloat(creditForm.total),
          low_balance_threshold_usd: parseFloat(creditForm.threshold) || 1.00,
          notes: creditForm.notes
        })
      })
      setShowCreditModal(false)
      fetchUsage()
    } catch (e) {
      console.error('Failed to save credit config:', e)
    } finally {
      setSavingCredit(false)
    }
  }

  if (loadingUsage) {
    return (
      <SectionCard
        title="AI Usage & Spend"
        badge="Loading..."
        icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>}
      >
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        </div>
      </SectionCard>
    )
  }

  if (!usage) {
    return (
      <SectionCard
        title="AI Usage & Spend"
        badge="No data"
        icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>}
      >
        <p className="text-sm text-gray-400 dark:text-gray-500 py-6 text-center">No AI usage data available yet. Usage will be tracked as AI features are used.</p>
      </SectionCard>
    )
  }

  const { daily, monthly, yearly, recent, credit } = usage

  return (
    <>
      {/* Low balance alert */}
      {credit?.lowBalance && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 border-l-4 border-l-red-500 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-300">Low AI Credit Balance</p>
              <p className="text-xs text-red-600 dark:text-red-400">Estimated remaining: {formatCost(credit.remaining)} (threshold: {formatCost(credit.threshold)})</p>
            </div>
          </div>
          <a href="https://platform.claude.com/settings/billing" target="_blank" rel="noopener noreferrer" className="btn-primary text-sm flex-shrink-0">
            Top Up on Anthropic
          </a>
        </div>
      )}

      {/* Credit balance card */}
      {credit && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">API Credit Balance</h3>
            <div className="flex items-center gap-2">
              <ExternalLink href="https://console.anthropic.com">Anthropic Console</ExternalLink>
              <button onClick={() => { setCreditForm({ total: credit.totalCredits.toString(), threshold: credit.threshold.toString(), notes: credit.notes || '' }); setShowCreditModal(true) }} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Deposited</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">${credit.totalCredits.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Spent (YTD)</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{formatCost(yearly.totalCost)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Estimated Remaining</p>
              <p className={`text-lg font-bold ${credit.lowBalance ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                {formatCost(credit.remaining)}
              </p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3">
            <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${credit.lowBalance ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${Math.max(0, Math.min(100, (credit.remaining / credit.totalCredits) * 100))}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <p className="text-[10px] text-gray-400">Alert threshold: ${credit.threshold.toFixed(2)}</p>
              <p className="text-[10px] text-gray-400">Last topped up: {credit.lastToppedUp ? new Date(credit.lastToppedUp).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>
        </div>
      )}

      {!credit && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Set up credit tracking to monitor your Anthropic API balance.</p>
          <button onClick={() => { setCreditForm({ total: '', threshold: '1.00', notes: '' }); setShowCreditModal(true) }} className="btn-primary text-sm">
            Configure Credit Balance
          </button>
        </div>
      )}

      {/* Token & spend metrics */}
      <SectionCard
        title="AI Usage & Spend"
        badge={`${yearly.callCount} calls YTD`}
        icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>}
      >
        <div className="py-3">
          {/* Period metrics */}
          <div className="grid grid-cols-3 gap-4 mb-5">
            {[
              { label: 'Today', data: daily },
              { label: 'This Month', data: monthly },
              { label: 'Year to Date', data: yearly }
            ].map(({ label, data }) => (
              <div key={label} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{label}</p>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Tokens In</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatTokens(data.inputTokens)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Tokens Out</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatTokens(data.outputTokens)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 pt-1.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Total Tokens</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{formatTokens(data.totalTokens)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Est. Cost</span>
                    <span className="text-sm font-bold text-primary-600 dark:text-primary-400">{formatCost(data.totalCost)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">API Calls</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{data.callCount}</span>
                  </div>
                  {data.errorCount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-red-500">Errors</span>
                      <span className="text-sm font-medium text-red-600 dark:text-red-400">{data.errorCount}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Usage by model */}
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Usage by Model (MTD)</h4>
          <div className="space-y-2 mb-5">
            {Object.entries(monthly.byModel || {}).sort((a, b) => b[1].cost - a[1].cost).map(([model, data]) => (
              <div key={model} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-900 dark:text-white">{MODEL_SHORT_NAMES[model] || model}</span>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">{model}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{data.calls} calls</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{formatTokens(data.inputTokens + data.outputTokens)} tokens</span>
                  <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">{formatCost(data.cost)}</span>
                </div>
              </div>
            ))}
            {Object.keys(monthly.byModel || {}).length === 0 && (
              <p className="text-xs text-gray-400 text-center py-2">No API calls this month</p>
            )}
          </div>

          {/* Recent calls */}
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Recent API Calls</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Model</th>
                  <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Mode</th>
                  <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">In</th>
                  <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Out</th>
                  <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Cost</th>
                  <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Time</th>
                  <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {(recent || []).map((r, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="py-1.5 px-2 text-gray-900 dark:text-white font-medium">{MODEL_SHORT_NAMES[r.model] || r.model}</td>
                    <td className="py-1.5 px-2 text-gray-500 dark:text-gray-400">{r.mode || '\u2014'}</td>
                    <td className="py-1.5 px-2 text-right text-gray-700 dark:text-gray-300">{formatTokens(r.input_tokens)}</td>
                    <td className="py-1.5 px-2 text-right text-gray-700 dark:text-gray-300">{formatTokens(r.output_tokens)}</td>
                    <td className="py-1.5 px-2 text-right text-primary-600 dark:text-primary-400">{formatCost(r.estimated_cost_usd)}</td>
                    <td className="py-1.5 px-2 text-right text-gray-500 dark:text-gray-400">{r.duration_ms ? `${r.duration_ms}ms` : '\u2014'}</td>
                    <td className="py-1.5 px-2 text-right text-gray-400 dark:text-gray-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  </tr>
                ))}
                {(recent || []).length === 0 && (
                  <tr><td colSpan={7} className="py-4 text-center text-gray-400">No recent calls</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Model pricing reference */}
          {usage.modelPricing && (
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/50">
              <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Model Pricing (per 1M tokens)</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(usage.modelPricing).map(([model, pricing]) => (
                  <span key={model} className="text-[10px] text-gray-500 dark:text-gray-400">
                    <span className="font-medium">{MODEL_SHORT_NAMES[model] || model}:</span> ${pricing.input} in / ${pricing.output} out
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Credit config modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Configure AI Credit Balance</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Enter your total Anthropic API credit balance. We'll track estimated spend and alert you when the balance drops below the threshold.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Credits (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  value={creditForm.total}
                  onChange={e => setCreditForm(prev => ({ ...prev, total: e.target.value }))}
                  placeholder="e.g., 100.00"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1">Check your balance at <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">console.anthropic.com</a></p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Low Balance Alert Threshold (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  value={creditForm.threshold}
                  onChange={e => setCreditForm(prev => ({ ...prev, threshold: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={creditForm.notes}
                  onChange={e => setCreditForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="e.g., Topped up via Anthropic dashboard"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowCreditModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleSaveCredit} disabled={savingCredit || !creditForm.total} className="btn-primary">
                {savingCredit ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const PLATFORM_MODELS = [
  { model: 'claude-opus-4-6', role: 'Tier 1 — Global policy generation', endpoint: '/api/ai/policy-generate' },
  { model: 'claude-sonnet-4-6', role: 'Tier 2/3 — Analysis & enrichment', endpoint: '/api/ai/policy-generate' },
  { model: 'claude-haiku-4-5-20251001', role: 'Quality scan & CDM classification', endpoint: '/api/ai/cdm-classify' }
]

function AIModelHealthSection() {
  const [statuses, setStatuses] = useState({})
  const [checking, setChecking] = useState(null)
  const [checkingAll, setCheckingAll] = useState(false)
  const [lastFullCheck, setLastFullCheck] = useState(() => localStorage.getItem('ssa_model_health_last_check'))

  const getToken = async () => {
    const { supabase } = await import('../../../shared/lib/supabase')
    return (await supabase.auth.getSession()).data.session?.access_token
  }

  const checkModel = async (m) => {
    setChecking(m.model)
    try {
      const token = await getToken()
      const body = m.endpoint === '/api/ai/cdm-classify'
        ? { name: 'Health Check Test', health_check_model_override: m.model }
        : { mode: 'flag_quality', structured_content: { purpose: 'Health check test.' }, health_check_model_override: m.model }

      const res = await fetch(m.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      })

      const data = await res.json()
      if (res.ok) {
        setStatuses(prev => ({ ...prev, [m.model]: { status: 'healthy', message: null, checkedAt: new Date().toISOString() } }))
      } else if (res.status === 404 || (data.error && data.error.includes('model_not_found'))) {
        setStatuses(prev => ({ ...prev, [m.model]: { status: 'error', message: 'Model not found \u2014 update model string', checkedAt: new Date().toISOString() } }))
      } else {
        setStatuses(prev => ({ ...prev, [m.model]: { status: 'warning', message: (data.error || 'Unknown error').slice(0, 80), checkedAt: new Date().toISOString() } }))
      }
    } catch (err) {
      setStatuses(prev => ({ ...prev, [m.model]: { status: 'warning', message: err.message?.slice(0, 80), checkedAt: new Date().toISOString() } }))
    } finally {
      setChecking(null)
    }
  }

  const checkAll = async () => {
    setCheckingAll(true)
    for (let i = 0; i < PLATFORM_MODELS.length; i++) {
      await checkModel(PLATFORM_MODELS[i])
      if (i < PLATFORM_MODELS.length - 1) await new Promise(r => setTimeout(r, 1000))
    }
    const ts = new Date().toISOString()
    setLastFullCheck(ts)
    localStorage.setItem('ssa_model_health_last_check', ts)
    setCheckingAll(false)
  }

  const statusBadge = (s) => {
    if (!s) return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">Unchecked</span>
    if (s.status === 'healthy') return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Healthy</span>
    if (s.status === 'error') return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Error</span>
    return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">Warning</span>
  }

  return (
    <SectionCard
      title="AI Model Health"
      badge={lastFullCheck ? `Last full check: ${new Date(lastFullCheck).toLocaleTimeString()}` : 'Never checked'}
      icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" /></svg>}
    >
      <div className="py-3">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">Live status of Anthropic models configured for this platform.</p>
          <button
            onClick={checkAll}
            disabled={checkingAll}
            className="btn-secondary text-xs flex items-center gap-1.5"
          >
            {checkingAll ? (
              <><div className="animate-spin rounded-full h-3 w-3 border-2 border-primary-500 border-t-transparent" /> Checking...</>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Run All Checks
              </>
            )}
          </button>
        </div>

        <div className="space-y-2">
          {PLATFORM_MODELS.map(m => {
            const s = statuses[m.model]
            const isChecking = checking === m.model
            return (
              <div key={m.model} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{MODEL_SHORT_NAMES[m.model] || m.model}</span>
                    <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 hidden sm:inline">{m.model}</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{m.role}</p>
                  {s?.message && <p className="text-xs text-red-500 dark:text-red-400 mt-0.5 truncate">{s.message}</p>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <p className="text-[10px] text-gray-400">{s?.checkedAt ? new Date(s.checkedAt).toLocaleTimeString() : 'Never'}</p>
                  </div>
                  {statusBadge(s)}
                  <button
                    onClick={() => checkModel(m)}
                    disabled={isChecking || checkingAll}
                    className="text-xs px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                  >
                    {isChecking ? <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-400 border-t-transparent" /> : 'Check'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          Anthropic deprecation notices appear in API response headers before a model is removed. Health checks will surface warnings automatically when Anthropic begins the deprecation process. Run checks regularly or after Anthropic announcements. Update model strings in <span className="font-mono text-gray-600 dark:text-gray-300">functions/lib/models.js</span> \u2014 changes apply to all endpoints simultaneously.
        </div>
      </div>
    </SectionCard>
  )
}

export default function GlobalAppStatus() {
  const { results, loading, lastChecked, runChecks, summary } = useAppStatus()
  const [activeTab, setActiveTab] = useState('infrastructure')

  // Compute avg latency from infrastructure + API results
  const avgLatency = (() => {
    if (!results) return null
    const latencies = [
      ...results.infrastructure.map(s => s.result.latency),
      ...results.apis.map(s => s.result.latency)
    ].filter(l => l != null)
    if (latencies.length === 0) return null
    return Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length)
  })()

  const checkedCount = results
    ? results.infrastructure.length + results.apis.filter(a => a.result.status !== 'skipped').length
    : 0

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Platform Metrics</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Infrastructure monitoring, AI usage tracking, and service health.
        </p>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('infrastructure')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'infrastructure'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
          </svg>
          Infrastructure
        </button>
        <button
          onClick={() => setActiveTab('ai')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'ai'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
          </svg>
          AI Usage Metrics
        </button>
      </div>

      {/* ── Infrastructure Tab ── */}
      {activeTab === 'infrastructure' && (
        <>
          {/* Loading state */}
          {!results && loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 dark:border-gray-700 border-t-primary-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Running health checks...</p>
            </div>
          )}

          {results && (
            <>
              {/* Overall status hero */}
              <OverallHero
                summary={summary}
                lastChecked={lastChecked}
                loading={loading}
                onRefresh={runChecks}
              />

              {/* Quick metric cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              label="Services Up"
              value={`${summary.operational}/${checkedCount}`}
              sub={summary.outage > 0 ? `${summary.outage} down` : 'All healthy'}
              status={summary.outage > 0 ? 'outage' : summary.degraded > 0 ? 'degraded' : 'operational'}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <MetricCard
              label="Avg Response"
              value={avgLatency != null ? `${avgLatency}ms` : '—'}
              sub={avgLatency != null ? (avgLatency < 200 ? 'Excellent' : avgLatency < 500 ? 'Normal' : 'Slow') : 'No data'}
              status={avgLatency != null ? (avgLatency < 200 ? 'operational' : avgLatency < 500 ? 'degraded' : 'outage') : 'unknown'}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
            />
            <MetricCard
              label="API Endpoints"
              value={results.apis.length}
              sub={`${results.apis.filter(a => a.result.status !== 'skipped').length} monitored`}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              }
            />
            <MetricCard
              label="App Routes"
              value={results.pages.length}
              sub="All registered"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zm12 0a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              }
            />
          </div>

          {/* Infrastructure + APIs side by side on large screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <SectionCard
              title="Infrastructure"
              badge={`${results.infrastructure.length} services`}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
              }
            >
              {results.infrastructure.map(svc => (
                <ServiceRow key={svc.id} name={svc.name} description={svc.description} result={svc.result} statusPage={svc.statusPage} />
              ))}
            </SectionCard>

            <SectionCard
              title="API Endpoints"
              badge={`${results.apis.length} endpoints`}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                </svg>
              }
            >
              {results.apis.map(ep => (
                <ServiceRow
                  key={ep.id}
                  name={ep.name}
                  description={ep.description || ep.path}
                  result={ep.result}
                />
              ))}
            </SectionCard>
          </div>

          {/* Application Pages */}
          <SectionCard
            title="Application Routes"
            badge={`${results.pages.length} routes`}
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          >
            <PageGrid pages={results.pages} />
          </SectionCard>

          {/* Technology Integrations */}
          <SectionCard
            title="Technology Integrations"
            badge={`7 integrations`}
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            }
          >
            <IntegrationCards />
            <p className="text-xs text-gray-400 dark:text-gray-500 pb-3">
              Detailed integration monitoring will be available in the upcoming Integrations section.
            </p>
          </SectionCard>

          {/* Reference Data Health */}
          {(() => {
            const health = calculateDataSourceHealth()
            if (!health) return null
            const { sources, summary } = health
            const statusColors = {
              overdue: 'bg-red-500', due: 'bg-yellow-500', upcoming: 'bg-blue-500',
              'not-ingested': 'bg-gray-400', current: 'bg-green-500'
            }
            const statusBadgeStyles = {
              overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
              due: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
              upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
              'not-ingested': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
              current: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            }
            const priorityStyles = {
              critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
              high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
              medium: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
            }

            return (
              <SectionCard
                title="Reference Data Health"
                badge={`${summary.total} sources`}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
                  </svg>
                }
              >
                <div className="py-3">
                  {/* Summary */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`w-2.5 h-2.5 rounded-full ${statusColors[summary.overallStatus === 'action-required' ? 'overdue' : summary.overallStatus === 'review-needed' ? 'due' : summary.overallStatus === 'updates-expected' ? 'upcoming' : 'current']}`} />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {summary.counts.current || 0} current
                      {summary.counts.upcoming ? `, ${summary.counts.upcoming} update expected` : ''}
                      {summary.counts.due ? `, ${summary.counts.due} due for review` : ''}
                      {summary.counts.overdue ? `, ${summary.counts.overdue} overdue` : ''}
                      {summary.counts['not-ingested'] ? `, ${summary.counts['not-ingested']} not integrated` : ''}
                    </span>
                  </div>

                  {/* Source rows */}
                  <div className="space-y-2">
                    {sources.map(s => (
                      <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                        <div className="flex-1 min-w-0 mr-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{s.name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusBadgeStyles[s.calculatedStatus]}`}>
                              {s.statusInfo?.label}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${priorityStyles[s.priority] || priorityStyles.medium}`}>
                              {s.priority}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {s.publisher} &middot; {s.refreshCadence}
                            {s.lastIngested ? ` \u00B7 Last ingested: ${new Date(s.lastIngested).toLocaleDateString()}` : ''}
                            {s.daysUntilExpected != null && s.daysUntilExpected < 0 ? ` \u00B7 ${Math.abs(s.daysUntilExpected)} days overdue` : ''}
                            {s.daysUntilExpected != null && s.daysUntilExpected >= 0 && s.daysUntilExpected <= 30 ? ` \u00B7 Expected in ${s.daysUntilExpected} days` : ''}
                          </p>
                        </div>
                        {s.checkUrl && (
                          <a href={s.checkUrl} target="_blank" rel="noopener noreferrer" className="text-gray-300 dark:text-gray-600 hover:text-primary-500 dark:hover:text-primary-400 transition-colors flex-shrink-0" title="Check for updates">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </SectionCard>
            )
          })()}
            </>
          )}
        </>
      )}

      {/* ── AI Usage Metrics Tab ── */}
      {activeTab === 'ai' && (
        <>
          <AIUsageSection />
          <AIModelHealthSection />
        </>
      )}
    </div>
  )
}
